'use client';

import React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { formatDate } from '@/utils/dateUtils';

interface EventCardProps {
  event: {
    id: string;
    title: string;
    date: string;
    time: string;
    location: string;
    category: string;
    image: string;
    description: string;
    registrationStatus: 'open' | 'closed' | 'full';
  };
}

const EventCard: React.FC<EventCardProps> = ({ event }) => {
  const getStatusColor = () => {
    switch (event.registrationStatus) {
      case 'open':
        return 'bg-success text-success-foreground';
      case 'closed':
        return 'bg-muted text-muted-foreground';
      case 'full':
        return 'bg-warning text-warning-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusText = () => {
    switch (event.registrationStatus) {
      case 'open':
        return 'Registration Open';
      case 'closed':
        return 'Registration Closed';
      case 'full':
        return 'Event Full';
      default:
        return 'Unknown';
    }
  };

  const formatDateValue = (dateString: string) => {
    return formatDate(dateString);
  };

  const getFirstImage = () => {
    if (!event.image) return '';
    try {
      const parsed = JSON.parse(event.image);
      return Array.isArray(parsed) ? parsed[0] : event.image;
    } catch {
      return event.image;
    }
  };

  return (
    <motion.div
      whileHover={{ scale: 1.03, y: -5 }}
      transition={{ duration: 0.3 }}
      className="bg-card rounded-lg overflow-hidden shadow-subtle hover:shadow-elevated transition-all duration-300 group"
    >
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={getFirstImage()}
          alt={event.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-4 right-4">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor()}`}>
            {getStatusText()}
          </span>
        </div>
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-accent text-accent-foreground">
            {event.category}
          </span>
        </div>
      </div>

      <div className="p-6">
        <h3 className="text-xl font-heading font-bold text-foreground mb-3 line-clamp-2 group-hover:text-primary transition-colors duration-200">
          {event.title}
        </h3>

        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
          {event.description}
        </p>

        <div className="space-y-2 mb-4">
          <div className="flex items-center space-x-2 text-sm text-foreground">
            <Icon name="CalendarIcon" size={20} className="text-primary" />
            <span className="font-medium">{formatDateValue(event.date)}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-foreground">
            <Icon name="ClockIcon" size={16} className="text-primary" />
            <span>{event.time}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-foreground">
            <Icon name="MapPinIcon" size={16} className="text-primary" />
            <span>{event.location}</span>
          </div>
        </div>

        <div className="flex items-center justify-end pt-4 border-t border-border">
          <Link
            href={`/events/${event.id}`}
            className="text-sm font-semibold text-primary hover:text-primary/80 transition-colors duration-200 flex items-center space-x-1"
          >
            <span>View Details</span>
            <Icon name="ArrowRightIcon" size={16} />
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

export default EventCard;